/**
 * Organization API — the clinic switcher's data source.
 *
 * Everything here is org-level metadata: clinic names, doctor counts, billing
 * preference. No clinical or patient data crosses an org boundary through this
 * service, by design — the only patient data the app ever sees is scoped to the
 * single active clinic, enforced server-side by RLS.
 */

import { apiRequest } from "./api.client";

export interface AccessibleClinic {
  clinic_id: string;
  name: string;
  location: string | null;
  is_active: boolean;
  /** The clinic on the user's own row — where they land with no selection. */
  is_home: boolean;
  /** Live doctor count. Display only; plans are flat per clinic, not per seat. */
  doctor_count: number;
  /** Non-clinician headcount. Doctors excluded so nobody is counted twice. */
  staff_count: number;
  /** False while a branch is still mid-onboarding. */
  setup_complete: boolean;
}

export type BillingPreference = "split" | "rollup";

export interface OrganizationSummary {
  organization_id: string;
  name: string;
  billing_preference: BillingPreference;
  clinic_count: number;
  created_at: string;
}

export interface OrgContext {
  is_org_owner: boolean;
  organization: OrganizationSummary | null;
  home_clinic_id: string | null;
  active_clinic_id: string | null;
  clinics: AccessibleClinic[];
}

export interface ActiveClinicResult {
  active_clinic_id: string;
  previous_clinic_id: string | null;
  clinic_name: string;
}

export interface AddClinicPayload {
  name: string;
  location: string;
  address?: string;
  phone?: string;
  email?: string;
  /** Only used on the first add, when the organization is created. */
  organization_name?: string;
}

export interface AddClinicResult {
  clinic: AccessibleClinic;
  organization: OrganizationSummary;
  /** True when this call is what made the caller an org owner. */
  organization_created: boolean;
}

export const OrganizationAPI = {
  /**
   * One unconditional boot call. Answers for owners AND ordinary staff, so no
   * caller needs to branch first: staff come back with is_org_owner=false and a
   * single-element clinic list, which is the signal to render no switcher.
   */
  getContext: (): Promise<OrgContext> =>
    apiRequest<OrgContext>("/organizations/me/context"),

  /**
   * Authorize and audit a deliberate switch.
   *
   * Do NOT call this directly from a component — go through ClinicContext's
   * `switchClinic`, which drains pending offline writes first and flushes the
   * local cache afterwards. Calling this alone flips the server-side audit
   * trail while leaving clinic A's PHI in IndexedDB.
   */
  setActiveClinic: (clinicId: string): Promise<ActiveClinicResult> =>
    apiRequest<ActiveClinicResult>("/organizations/me/active-clinic", {
      method: "POST",
      body: JSON.stringify({ clinic_id: clinicId }),
    }),

  /**
   * Add a branch clinic.
   *
   * NOT `POST /onboarding/clinic` — that route creates a user's first clinic,
   * 409s if they already have one, and reassigns `users.clinic_id` to the new
   * row, which would relocate the owner out of the clinic they are working in.
   *
   * On the very first add the backend also creates the organization around the
   * caller's existing clinic; `organization_created` reports that, since it is
   * the moment a switcher appears in their sidebar.
   */
  addClinic: (payload: AddClinicPayload): Promise<AddClinicResult> =>
    apiRequest<AddClinicResult>("/organizations/me/clinics", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /**
   * Rename the organization. Owner only.
   *
   * The name starts as a guess — derived from the first clinic with its
   * locality stripped — so this is the correction path, surfaced in
   * Settings > Clinic Settings.
   */
  rename: (name: string): Promise<OrganizationSummary> =>
    apiRequest<OrganizationSummary>("/organizations/me", {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  setBillingPreference: (
    preference: BillingPreference,
  ): Promise<OrganizationSummary> =>
    apiRequest<OrganizationSummary>("/organizations/me/billing-preference", {
      method: "PATCH",
      body: JSON.stringify({ billing_preference: preference }),
    }),
};
